// AdminAICredits.js — Admin AI Credits Control Panel
// 4 tabs: Dashboard stats | Pending Requests | Teachers Balance | Manage Packages

import React, { useEffect, useState, useCallback } from 'react';
import { aiCreditsAPI } from '../services/api';
import '../styles/AdminAICredits.css';
import {
  FaBrain,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBan,
  FaPlus,
  FaCoins,
  FaChartBar,
  FaUsers,
  FaBoxes,
  FaSpinner,
  FaEdit,
} from 'react-icons/fa';

// ─────── Helpers ───────
const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('ar-EG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_MAP = {
  pending:   { label: 'معلق',    icon: <FaClock />,       cls: 'pending' },
  approved:  { label: 'مقبول',   icon: <FaCheckCircle />, cls: 'approved' },
  rejected:  { label: 'مرفوض',  icon: <FaTimesCircle />, cls: 'rejected' },
  cancelled: { label: 'ملغي',    icon: <FaBan />,         cls: 'cancelled' },
};

const creditColorClass = (n) => {
  if (n === 0) return 'abt-credits-low';
  if (n < 50)  return 'abt-credits-mid';
  return 'abt-credits-ok';
};

// ─────── Manual Credit Modal ───────
const ManualCreditModal = ({ teacher, onClose, onSuccess }) => {
  const [amount, setAmount]   = useState('');
  const [desc, setDesc]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const handleSubmit = async () => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) { setErr('أدخل عدداً صحيحاً موجباً'); return; }
    setSaving(true); setErr('');
    try {
      await aiCreditsAPI.manualCredit({ teacher_id: teacher.id, amount: n, description: desc.trim() || undefined });
      onSuccess(`✅ تمت إضافة ${n} كريدت للمعلم ${teacher.full_name}`);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target.classList.contains('modal-overlay') && onClose()}>
      <div className="modal-box" dir="rtl">
        <h3><FaCoins style={{ color: '#f59e0b' }} /> إضافة رصيد يدوي</h3>
        <p style={{ fontSize: '0.88rem', color: '#6b7280', marginTop: '-0.5rem', marginBottom: '1rem' }}>
          المعلم: <strong>{teacher.full_name}</strong> — الرصيد الحالي:{' '}
          <strong style={{ color: '#4f46e5' }}>{teacher.ai_credits}</strong> كريدت
        </p>

        <div className="modal-form-group">
          <label>عدد الكريدت المُضافة *</label>
          <input
            type="number"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="مثال: 200"
            autoFocus
          />
        </div>

        <div className="modal-form-group">
          <label>ملاحظة داخلية (اختياري)</label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="مثال: دفع نقداً في المكتب — فاتورة رقم 123"
            maxLength={200}
          />
        </div>

        {err && (
          <div style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.75rem',
            background: '#fee2e2', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
            ❌ {err}
          </div>
        )}

        <div className="modal-actions">
          <button className="modal-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> جارٍ الإضافة...</> : <><FaPlus /> إضافة الرصيد</>}
          </button>
          <button className="modal-btn-cancel" onClick={onClose} type="button">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ─────── Main Component ───────
const AdminAICredits = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats]         = useState(null);
  const [requests, setRequests]   = useState([]);
  const [teachers, setTeachers]   = useState([]);
  const [packages, setPackages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState('');

  // Filters
  const [reqFilter, setReqFilter] = useState('pending');
  const [search, setSearch]       = useState('');

  // Processing state per request
  const [processingId, setProcessingId]     = useState(null);
  const [adminNotes, setAdminNotes]         = useState({});

  // Manual credit modal
  const [manualTarget, setManualTarget]     = useState(null);

  // Package edit modal
  const [editPkg, setEditPkg]               = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // ─── Fetch all data ───
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, rr, tr, pr] = await Promise.all([
        aiCreditsAPI.getStats(),
        aiCreditsAPI.getAllRequests({ status: reqFilter === 'all' ? undefined : reqFilter }),
        aiCreditsAPI.getTeachersBalance(),
        aiCreditsAPI.getAdminPackages(),
      ]);
      setStats(sr.data);
      setRequests(rr.data);
      setTeachers(tr.data);
      setPackages(pr.data);
    } catch (e) {
      console.error('[AdminAICredits]', e);
    } finally {
      setLoading(false);
    }
  }, [reqFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Refetch requests when filter changes ───
  const fetchRequests = useCallback(async () => {
    try {
      const r = await aiCreditsAPI.getAllRequests(
        reqFilter === 'all' ? {} : { status: reqFilter }
      );
      setRequests(r.data);
    } catch (e) { console.error(e); }
  }, [reqFilter]);

  useEffect(() => {
    if (activeTab === 'requests') fetchRequests();
  }, [reqFilter, activeTab, fetchRequests]);

  // ─── Process a request ───
  const handleProcess = async (reqId, action) => {
    setProcessingId(reqId);
    try {
      const res = await aiCreditsAPI.processRequest(reqId, {
        action,
        admin_note: adminNotes[reqId] || undefined,
      });
      showToast(res.data.message);
      fetchRequests();
      aiCreditsAPI.getStats().then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      showToast(e.response?.data?.message || 'حدث خطأ');
    } finally {
      setProcessingId(null);
    }
  };

  // ─── Toggle package active ───
  const handleTogglePkg = async (pkg) => {
    try {
      await aiCreditsAPI.updatePackage(pkg.id, { is_active: !pkg.is_active });
      setPackages((prev) => prev.map((p) => p.id === pkg.id ? { ...p, is_active: !p.is_active } : p));
    } catch (e) {
      alert('فشل تحديث الباقة');
    }
  };

  // ─── Filtered teachers ───
  const filteredTeachers = teachers.filter((t) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (t.full_name || '').toLowerCase().includes(s) || (t.email || '').toLowerCase().includes(s);
  });

  // ─── Pending count ───
  const pendingCount = stats?.pending_requests ?? 0;

  // ─── Weekly chart ───
  const weeklyData = stats?.weekly_usage ?? [];
  const maxVal = Math.max(...weeklyData.map((d) => Number(d.used)), 1);

  // ─────────────────────────────────────────────
  return (
    <div className="admin-ai-page">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '1.25rem', right: '50%', transform: 'translateX(50%)',
          background: '#1f2937', color: '#fff', padding: '0.75rem 1.5rem',
          borderRadius: 12, zIndex: 9999, fontFamily: 'Cairo,sans-serif',
          fontSize: '0.9rem', boxShadow: '0 8px 24px rgba(0,0,0,.25)',
          animation: 'modalIn 0.3s ease',
        }}>
          {toast}
        </div>
      )}

      {/* Manual Credit Modal */}
      {manualTarget && (
        <ManualCreditModal
          teacher={manualTarget}
          onClose={() => setManualTarget(null)}
          onSuccess={(msg) => {
            showToast(msg);
            aiCreditsAPI.getTeachersBalance().then((r) => setTeachers(r.data)).catch(() => {});
          }}
        />
      )}

      {/* ── Header ── */}
      <div className="admin-ai-header">
        <h1><FaBrain /> إدارة رصيد الذكاء الاصطناعي</h1>
        <p>مراقبة الأرصدة، معالجة طلبات الشحن، وإضافة الكريدت يدوياً للمعلمين.</p>
      </div>

      {/* ── Stats Cards ── */}
      {stats && (
        <div className="admin-ai-stats">
          <div className="admin-ai-stat-card purple">
            <div className="aas-label">إجمالي الكريدت الموزعة</div>
            <div className="aas-value">{Number(stats.total_distributed).toLocaleString()}</div>
            <div className="aas-sub">كريدت تصحيح</div>
          </div>
          <div className="admin-ai-stat-card orange">
            <div className="aas-label">طلبات معلقة</div>
            <div className="aas-value">{stats.pending_requests}</div>
            <div className="aas-sub">تحتاج مراجعة</div>
          </div>
          <div className="admin-ai-stat-card green">
            <div className="aas-label">إجمالي الكريدت المستهلكة</div>
            <div className="aas-value">{Number(stats.total_used).toLocaleString()}</div>
            <div className="aas-sub">تصحيح مجرى</div>
          </div>
          <div className="admin-ai-stat-card blue">
            <div className="aas-label">رصيد قيد الاستخدام</div>
            <div className="aas-value">{Number(stats.total_balance).toLocaleString()}</div>
            <div className="aas-sub">لدى المعلمين</div>
          </div>
          <div className="admin-ai-stat-card indigo">
            <div className="aas-label">معلمون نشطون</div>
            <div className="aas-value">{stats.active_teachers}</div>
            <div className="aas-sub">معلم</div>
          </div>
          <div className="admin-ai-stat-card red">
            <div className="aas-label">رصيد صفر</div>
            <div className="aas-value">{stats.zero_balance_teachers}</div>
            <div className="aas-sub">معلم بلا رصيد</div>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="admin-ai-tabs">
        <button
          className={`admin-ai-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
          type="button"
        >
          <FaChartBar /> لوحة المعلومات
        </button>
        <button
          className={`admin-ai-tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
          type="button"
        >
          <FaClock /> الطلبات
          {pendingCount > 0 && (
            <span className="admin-ai-tab-badge">{pendingCount}</span>
          )}
        </button>
        <button
          className={`admin-ai-tab ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
          type="button"
        >
          <FaUsers /> أرصدة المعلمين
        </button>
        <button
          className={`admin-ai-tab ${activeTab === 'packages' ? 'active' : ''}`}
          onClick={() => setActiveTab('packages')}
          type="button"
        >
          <FaBoxes /> الباقات
        </button>
      </div>

      {loading ? (
        <div className="admin-ai-loading">
          <FaSpinner style={{ animation: 'spin 1s linear infinite', marginLeft: 8 }} />
          جارٍ التحميل...
        </div>
      ) : (
        <>
          {/* ══════════════ TAB: DASHBOARD ══════════════ */}
          {activeTab === 'dashboard' && (
            <div className="admin-ai-card">
              <div className="admin-ai-card-title">
                <span><FaChartBar /> استخدام الكريدت (آخر 7 أيام)</span>
              </div>
              {weeklyData.length > 0 ? (
                <div className="admin-chart-wrap">
                  <div className="admin-chart-bars">
                    {weeklyData.map((d, i) => {
                      const pct = (Number(d.used) / maxVal) * 100;
                      return (
                        <div key={i} className="admin-chart-bar-wrap">
                          <div className="admin-chart-value">{d.used}</div>
                          <div className="admin-chart-bar" style={{ height: `${Math.max(pct, 3)}%` }} />
                          <div className="admin-chart-label">
                            {new Date(d.day).toLocaleDateString('ar-EG', { weekday: 'short' })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="admin-ai-empty">
                  <span className="empty-icon">📊</span>
                  لا توجد بيانات استخدام في آخر 7 أيام
                </div>
              )}

              {/* Summary table */}
              {stats && (
                <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{
                    background: 'linear-gradient(135deg,#ede9fe,#f5f3ff)',
                    borderRadius: 14, padding: '1rem 1.25rem',
                    border: '1px solid #c4b5fd',
                  }}>
                    <div style={{ fontSize: '0.82rem', color: '#5b21b6', marginBottom: 4 }}>معدل استهلاك الكريدت</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#4f46e5' }}>
                      {stats.active_teachers > 0
                        ? Math.round(stats.total_used / stats.active_teachers)
                        : 0}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>كريدت لكل معلم نشط</div>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg,#d1fae5,#ecfdf5)',
                    borderRadius: 14, padding: '1rem 1.25rem',
                    border: '1px solid #6ee7b7',
                  }}>
                    <div style={{ fontSize: '0.82rem', color: '#065f46', marginBottom: 4 }}>نسبة المعلمين بلا رصيد</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#059669' }}>
                      {stats.active_teachers > 0
                        ? Math.round((stats.zero_balance_teachers / stats.active_teachers) * 100)
                        : 0}%
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>من إجمالي المعلمين النشطين</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════ TAB: REQUESTS ══════════════ */}
          {activeTab === 'requests' && (
            <div className="admin-ai-card">
              <div className="admin-ai-card-title">
                <span><FaClock /> طلبات شحن الرصيد</span>
                <button
                  onClick={fetchRequests}
                  type="button"
                  style={{ fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                >
                  🔄 تحديث
                </button>
              </div>

              {/* Filter */}
              <div className="admin-ai-filter-tabs">
                {[
                  ['pending', 'معلق'],
                  ['approved', 'مقبول'],
                  ['rejected', 'مرفوض'],
                  ['cancelled', 'ملغي'],
                  ['all', 'الكل'],
                ].map(([val, lbl]) => (
                  <button
                    key={val}
                    className={`admin-ai-filter-tab ${reqFilter === val ? 'active' : ''}`}
                    onClick={() => setReqFilter(val)}
                    type="button"
                  >
                    {lbl}
                  </button>
                ))}
              </div>

              {requests.length === 0 ? (
                <div className="admin-ai-empty">
                  <span className="empty-icon">📭</span>
                  لا توجد طلبات بهذه الحالة
                </div>
              ) : (
                <div className="admin-req-list">
                  {requests.map((req) => {
                    const st = STATUS_MAP[req.status] || STATUS_MAP.pending;
                    const isProcessing = processingId === req.id;
                    return (
                      <div key={req.id} className="admin-req-item">
                        <div className="admin-req-top">
                          <div className="admin-req-teacher">
                            <div className="admin-req-avatar">
                              {(req.teacher_name?.charAt(0) || 'م').toUpperCase()}
                            </div>
                            <div>
                              <div className="admin-req-name">{req.teacher_name}</div>
                              <div className="admin-req-email">{req.teacher_email}</div>
                            </div>
                          </div>
                          <span className={`req-status-badge ${st.cls}`}>
                            {st.icon} {st.label}
                          </span>
                        </div>

                        <div className="admin-req-info" style={{ marginBottom: '0.75rem' }}>
                          <span className="admin-req-info-tag id">طلب #{req.id}</span>
                          <span className="admin-req-info-tag credits">
                            {req.credits_requested} كريدت
                          </span>
                          <span className="admin-req-info-tag price">
                            ${req.amount}
                          </span>
                          <span className="admin-req-info-tag">
                            {req.package_name || '—'}
                          </span>
                          <span className="admin-req-info-tag date">
                            {fmtDateTime(req.created_at)}
                          </span>
                        </div>

                        {req.teacher_note && (
                          <div className="admin-req-note">
                            💬 {req.teacher_note}
                          </div>
                        )}

                        {req.status === 'pending' && (
                          <div className="admin-req-actions">
                            <input
                              type="text"
                              placeholder="ملاحظة للمعلم (اختياري)..."
                              value={adminNotes[req.id] || ''}
                              onChange={(e) =>
                                setAdminNotes((prev) => ({ ...prev, [req.id]: e.target.value }))
                              }
                            />
                            <button
                              className="btn-approve"
                              onClick={() => handleProcess(req.id, 'approve')}
                              disabled={isProcessing}
                            >
                              {isProcessing
                                ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                : <FaCheckCircle />}
                              قبول وإضافة الرصيد
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleProcess(req.id, 'reject')}
                              disabled={isProcessing}
                            >
                              <FaTimesCircle /> رفض
                            </button>
                          </div>
                        )}

                        {req.admin_note && req.status !== 'pending' && (
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                            ملاحظة الإدارة: <em>{req.admin_note}</em>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══════════════ TAB: BALANCE ══════════════ */}
          {activeTab === 'balance' && (
            <div className="admin-ai-card">
              <div className="admin-ai-card-title">
                <span><FaUsers /> أرصدة جميع المعلمين</span>
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  {filteredTeachers.length} معلم
                </span>
              </div>

              <div className="admin-ai-search">
                <FaSearch style={{ color: '#9ca3af', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="بحث بالاسم أو البريد الإلكتروني..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {filteredTeachers.length === 0 ? (
                <div className="admin-ai-empty">
                  <span className="empty-icon">👥</span>
                  لا يوجد معلمون مطابقون
                </div>
              ) : (
                <div className="admin-balance-table-wrap">
                  <table className="admin-balance-table">
                    <thead>
                      <tr>
                        <th>المعلم</th>
                        <th>الرصيد الحالي</th>
                        <th>المستهلك</th>
                        <th>الحالة</th>
                        <th>آخر شحن</th>
                        <th>إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTeachers.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div className="abt-avatar">
                                {(t.full_name?.charAt(0) || 'م').toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.full_name}</div>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{t.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={creditColorClass(t.ai_credits)}>
                              {(t.ai_credits || 0).toLocaleString()}
                            </span>
                          </td>
                          <td style={{ color: '#6b7280' }}>
                            {(t.ai_credits_used || 0).toLocaleString()}
                          </td>
                          <td>
                            <span style={{
                              padding: '3px 10px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 700,
                              background: t.status === 'active' ? '#d1fae5' : t.status === 'pending' ? '#fef3c7' : '#fee2e2',
                              color: t.status === 'active' ? '#065f46' : t.status === 'pending' ? '#92400e' : '#991b1b',
                            }}>
                              {t.status === 'active' ? 'نشط' : t.status === 'pending' ? 'قيد المراجعة' : 'موقوف'}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            {fmt(t.last_top_up_at) || '—'}
                          </td>
                          <td>
                            <button
                              className="btn-add-manual"
                              onClick={() => setManualTarget(t)}
                              type="button"
                            >
                              <FaPlus /> إضافة رصيد
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══════════════ TAB: PACKAGES ══════════════ */}
          {activeTab === 'packages' && (
            <div className="admin-ai-card">
              <div className="admin-ai-card-title">
                <span><FaBoxes /> إدارة الباقات</span>
              </div>

              <div className="admin-pkg-grid">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="admin-pkg-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div className="admin-pkg-card-name">{pkg.name}</div>
                      <span className={`pkg-active-badge ${pkg.is_active ? 'active' : 'inactive'}`}>
                        {pkg.is_active ? 'نشط' : 'موقوف'}
                      </span>
                    </div>
                    <div className="admin-pkg-card-detail">
                      💳 {pkg.credits} كريدت — ${pkg.price}
                    </div>
                    {pkg.badge && (
                      <div style={{ fontSize: '0.78rem', color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '2px 8px', display: 'inline-block', marginTop: 4 }}>
                        {pkg.badge}
                      </div>
                    )}
                    {pkg.description && (
                      <div className="admin-pkg-card-detail" style={{ marginTop: 6, lineHeight: 1.5 }}>
                        {pkg.description}
                      </div>
                    )}
                    <div className="admin-pkg-card-actions">
                      <button
                        className="btn-outline-sm"
                        onClick={() => handleTogglePkg(pkg)}
                        type="button"
                      >
                        {pkg.is_active ? '⛔ إيقاف' : '✅ تفعيل'}
                      </button>
                      <button
                        className="btn-outline-sm"
                        onClick={() => setEditPkg(pkg)}
                        type="button"
                        style={{ color: '#4f46e5', borderColor: '#c4b5fd' }}
                      >
                        <FaEdit /> تعديل
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Inline edit form */}
              {editPkg && (
                <div className="modal-overlay" onMouseDown={(e) => e.target.classList.contains('modal-overlay') && setEditPkg(null)}>
                  <div className="modal-box" dir="rtl">
                    <h3><FaEdit /> تعديل الباقة: {editPkg.name}</h3>
                    {[
                      ['name', 'الاسم', 'text'],
                      ['credits', 'عدد الكريدت', 'number'],
                      ['price', 'السعر ($)', 'number'],
                      ['badge', 'الشارة (اختياري)', 'text'],
                    ].map(([field, label, type]) => (
                      <div className="modal-form-group" key={field}>
                        <label>{label}</label>
                        <input
                          type={type}
                          value={editPkg[field] ?? ''}
                          onChange={(e) => setEditPkg((p) => ({ ...p, [field]: e.target.value }))}
                        />
                      </div>
                    ))}
                    <div className="modal-form-group">
                      <label>الوصف (اختياري)</label>
                      <textarea
                        value={editPkg.description ?? ''}
                        onChange={(e) => setEditPkg((p) => ({ ...p, description: e.target.value }))}
                      />
                    </div>
                    <div className="modal-actions">
                      <button
                        className="modal-btn-primary"
                        onClick={async () => {
                          try {
                            await aiCreditsAPI.updatePackage(editPkg.id, editPkg);
                            showToast('✅ تم تحديث الباقة');
                            const r = await aiCreditsAPI.getAdminPackages();
                            setPackages(r.data);
                            setEditPkg(null);
                          } catch (e) { alert('فشل التحديث'); }
                        }}
                        type="button"
                      >
                        حفظ التغييرات
                      </button>
                      <button className="modal-btn-cancel" onClick={() => setEditPkg(null)} type="button">
                        إلغاء
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAICredits;
